import { managedModelIds } from "./aiProviders";

export type ModelPricing = {
  inputPerMillion: number;
  outputPerMillion: number;
  cacheReadPerMillion?: number;
  cacheWritePerMillion?: number;
};

// Providers meter usage under namespaced ids: Bedrock uses region-scoped
// inference profiles (`us.anthropic.claude-opus-4-8`) and OpenRouter uses a
// `<vendor>/<model>` slug (`anthropic/claude-sonnet-5`). Strip the prefix in
// either form so pricing keyed by the bare model id still resolves. Returns the
// id unchanged otherwise.
export function canonicalPricingModelId(model: string): string {
  const bare = model.includes("/")
    ? model.slice(model.lastIndexOf("/") + 1)
    : model;
  const match = /^(?:[a-z][a-z0-9-]*\.)?anthropic\.(.+)$/.exec(bare);
  return match ? match[1]! : bare;
}

// List prices (USD per 1M tokens) for well-known models, keyed by the raw model
// id the provider returns (the dimension usage meters), used to seed connector
// pricing defaults; a user-entered rate always overrides. Model ids:
// platform.claude.com/docs/en/about-claude/models/overview and
// developers.openai.com/api/docs/models/all
export const DEFAULT_MODEL_PRICING: Record<string, ModelPricing> = {
  "claude-fable-5-1": {
    inputPerMillion: 10,
    outputPerMillion: 50,
    cacheReadPerMillion: 0.25,
    cacheWritePerMillion: 12.5,
  },
  "claude-fable-5": {
    inputPerMillion: 10,
    outputPerMillion: 50,
    cacheReadPerMillion: 1,
    cacheWritePerMillion: 12.5,
  },
  "claude-sonnet-5-5": {
    inputPerMillion: 2,
    outputPerMillion: 10,
    cacheReadPerMillion: 0.2,
    cacheWritePerMillion: 2.5,
  },
  "claude-sonnet-5": {
    inputPerMillion: 2,
    outputPerMillion: 10,
    cacheReadPerMillion: 0.2,
    cacheWritePerMillion: 2.5,
  },
  "claude-opus-5-5": {
    inputPerMillion: 4,
    outputPerMillion: 20,
    cacheReadPerMillion: 0.2,
    cacheWritePerMillion: 5,
  },
  "claude-opus-5": {
    inputPerMillion: 5,
    outputPerMillion: 25,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "claude-opus-4-8": {
    inputPerMillion: 5,
    outputPerMillion: 25,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "claude-opus-4-7": {
    inputPerMillion: 5,
    outputPerMillion: 25,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "claude-opus-4-6": {
    inputPerMillion: 5,
    outputPerMillion: 25,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "claude-opus-4-6-v1": {
    inputPerMillion: 5,
    outputPerMillion: 25,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "claude-sonnet-4-6": {
    inputPerMillion: 3,
    outputPerMillion: 15,
    cacheReadPerMillion: 0.3,
    cacheWritePerMillion: 3.75,
  },
  "claude-opus-4-5": {
    inputPerMillion: 5,
    outputPerMillion: 25,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "claude-opus-4-5-20251101": {
    inputPerMillion: 5,
    outputPerMillion: 25,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "claude-opus-4-5-20251101-v1:0": {
    inputPerMillion: 5,
    outputPerMillion: 25,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  // Prompts over 100k tokens bill at 5x these base-tier rates.
  "claude-haiku-5-5": {
    inputPerMillion: 0.1,
    outputPerMillion: 0.5,
    cacheReadPerMillion: 0.01,
    cacheWritePerMillion: 0.125,
  },
  "claude-haiku-4-5": {
    inputPerMillion: 1,
    outputPerMillion: 5,
    cacheReadPerMillion: 0.1,
    cacheWritePerMillion: 1.25,
  },
  "claude-haiku-4-5-20251001": {
    inputPerMillion: 1,
    outputPerMillion: 5,
    cacheReadPerMillion: 0.1,
    cacheWritePerMillion: 1.25,
  },
  "claude-haiku-4-5-20251001-v1:0": {
    inputPerMillion: 1,
    outputPerMillion: 5,
    cacheReadPerMillion: 0.1,
    cacheWritePerMillion: 1.25,
  },
  "claude-sonnet-4-5": {
    inputPerMillion: 3,
    outputPerMillion: 15,
    cacheReadPerMillion: 0.3,
    cacheWritePerMillion: 3.75,
  },
  "claude-sonnet-4-5-20250929": {
    inputPerMillion: 3,
    outputPerMillion: 15,
    cacheReadPerMillion: 0.3,
    cacheWritePerMillion: 3.75,
  },
  "claude-sonnet-4-5-20250929-v1:0": {
    inputPerMillion: 3,
    outputPerMillion: 15,
    cacheReadPerMillion: 0.3,
    cacheWritePerMillion: 3.75,
  },
  "gpt-6-astra": {
    inputPerMillion: 10,
    outputPerMillion: 50,
    cacheReadPerMillion: 1,
    cacheWritePerMillion: 12.5,
  },
  "gpt-6.1-sol": {
    inputPerMillion: 2,
    outputPerMillion: 10,
    cacheReadPerMillion: 0.1,
    cacheWritePerMillion: 2.5,
  },
  "gpt-6-sol": {
    inputPerMillion: 2,
    outputPerMillion: 10,
    cacheReadPerMillion: 0.2,
    cacheWritePerMillion: 2.5,
  },
  "gpt-6-luna": {
    inputPerMillion: 0.1,
    outputPerMillion: 0.5,
    cacheReadPerMillion: 0.01,
    cacheWritePerMillion: 0.125,
  },
  "gpt-5.6-sol": {
    inputPerMillion: 5,
    outputPerMillion: 30,
    cacheReadPerMillion: 0.5,
    cacheWritePerMillion: 6.25,
  },
  "gpt-5.6-terra": {
    inputPerMillion: 2,
    outputPerMillion: 12,
    cacheReadPerMillion: 0.2,
    cacheWritePerMillion: 2.5,
  },
  "gpt-5.6-luna": {
    inputPerMillion: 0.2,
    outputPerMillion: 1.2,
    cacheReadPerMillion: 0.02,
    cacheWritePerMillion: 0.25,
  },
  "gpt-5.5-pro": { inputPerMillion: 30, outputPerMillion: 180 },
  "gpt-5.5": {
    inputPerMillion: 5,
    outputPerMillion: 30,
    cacheReadPerMillion: 0.5,
  },
  "gpt-5.4-pro": { inputPerMillion: 30, outputPerMillion: 180 },
  "gpt-5.4": {
    inputPerMillion: 2.5,
    outputPerMillion: 15,
    cacheReadPerMillion: 0.25,
  },
  "gpt-5.4-mini": {
    inputPerMillion: 0.75,
    outputPerMillion: 4.5,
    cacheReadPerMillion: 0.075,
  },
  "gpt-5.4-nano": {
    inputPerMillion: 0.2,
    outputPerMillion: 1.25,
    cacheReadPerMillion: 0.02,
  },
  "gpt-5.3-codex": {
    inputPerMillion: 1.75,
    outputPerMillion: 14,
    cacheReadPerMillion: 0.175,
  },
  "gpt-5.2": {
    inputPerMillion: 1.75,
    outputPerMillion: 14,
    cacheReadPerMillion: 0.175,
  },
  "gpt-5.1": {
    inputPerMillion: 1.25,
    outputPerMillion: 10,
    cacheReadPerMillion: 0.125,
  },
  "gpt-5": {
    inputPerMillion: 1.25,
    outputPerMillion: 10,
    cacheReadPerMillion: 0.125,
  },
  "gpt-5-mini": {
    inputPerMillion: 0.25,
    outputPerMillion: 2,
    cacheReadPerMillion: 0.025,
  },
  "gpt-5-nano": {
    inputPerMillion: 0.05,
    outputPerMillion: 0.4,
    cacheReadPerMillion: 0.005,
  },
};

// Managed usage has no aiProviderId, so pricing resolves through bare model ids.
export const MANAGED_MODEL_PRICING_ESTIMATES: Record<string, ModelPricing> =
  Object.fromEntries(
    managedModelIds.map((model) => [model, DEFAULT_MODEL_PRICING[model]!])
  );
